import { useCallback, useContext } from 'react';

import { fg } from '@confluence/feature-gating';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

export const TWC_AIFC_QUERY_PARAMS = {
	openModal: 'aifc-onboarding',
	entryPoint: 'twc-aifc-onboarding',
} as const;

export const useShouldShowTwcAifcOnboarding = (): {
	shouldShow: boolean;
	loading: boolean;
} => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();
	const hasMatchingQueryParams =
		queryParams.openModal === TWC_AIFC_QUERY_PARAMS.openModal &&
		queryParams.entryPoint === TWC_AIFC_QUERY_PARAMS.entryPoint;
	const skip = !fg('twcg-1881-twc-xflow-ai-led-onboarding-gate') || !hasMatchingQueryParams;

	const { data, loading } = useGetOnboardingState(
		[AIFC_ONBOARDING_STATE_KEYS.hasSeenTwcAifcOnboarding],
		skip,
	);

	if (skip) {
		return { shouldShow: false, loading: false };
	}
	if (loading) {
		return { shouldShow: false, loading: true };
	}
	const state = deserializeState(data);
	return { shouldShow: !state.hasSeenTwcAifcOnboarding, loading: false };
};

export const useMarkTwcAifcOnboardingComplete = (): (() => void) => {
	const { setOnboardingState } = useSetOnboardingState();
	return useCallback(() => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasSeenTwcAifcOnboarding,
			value: 'true',
		});
	}, [setOnboardingState]);
};
